import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { PageHeader } from '../components/layout/PageHeader';
import api from '../lib/api';
import { toast } from 'sonner';
import { TrendingUp, Clock, Target, Calendar, ArrowRight, CheckCircle } from 'lucide-react';

const ProgressBar = ({ value, color = 'bg-blue-500' }) => (
  <div className="w-full bg-slate-200 rounded-full h-2">
    <div 
      className={`${color} h-2 rounded-full transition-all duration-500`} 
      style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
    />
  </div>
);

export default function ComplianceMetricsPage() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      const response = await api.get('/metrics/time-to-compliance');
      setMetrics(response.data);
    } catch (error) {
      toast.error('Failed to load compliance metrics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      </DashboardLayout>
    );
  }

  const getFrameworkColor = (name) => {
    const colors = {
      'SOC 2': 'bg-blue-500',
      'ISO 27001': 'bg-purple-500',
      'GDPR': 'bg-green-500',
      'HIPAA': 'bg-orange-500'
    };
    return colors[name] || 'bg-slate-500';
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="Time to Compliance"
        subtitle="Track your compliance velocity and estimated completion dates"
        icon={<TrendingUp size={28} />}
      />

      {/* Overall Progress */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-sm p-8 mb-8 text-white">
        <div className="grid grid-cols-4 gap-8">
          <div>
            <div className="text-sm text-slate-400 mb-1">Overall Compliance</div>
            <div className="text-4xl font-bold">{metrics?.overall?.compliance_percentage || 0}%</div>
            <ProgressBar value={metrics?.overall?.compliance_percentage || 0} color="bg-emerald-500" />
          </div>
          <div>
            <div className="text-sm text-slate-400 mb-1">Controls Completed</div>
            <div className="text-4xl font-bold">
              {metrics?.overall?.compliant_controls || 0}
              <span className="text-lg text-slate-400">/{metrics?.overall?.total_controls || 0}</span>
            </div>
            <div className="text-sm text-slate-400 mt-1">
              {metrics?.overall?.remaining_controls || 0} remaining
            </div>
          </div>
          <div>
            <div className="text-sm text-slate-400 mb-1">Velocity</div>
            <div className="text-4xl font-bold">
              {metrics?.overall?.velocity?.controls_per_week || 0}
              <span className="text-lg text-slate-400">/week</span>
            </div>
            <div className="text-sm text-slate-400 mt-1">
              {metrics?.overall?.velocity?.controls_last_30_days || 0} in last 30 days
            </div>
          </div>
          <div>
            <div className="text-sm text-slate-400 mb-1">Est. Completion</div>
            <div className="text-2xl font-bold">
              {metrics?.overall?.estimated_completion_date || 'Calculating...'}
            </div>
            <div className="text-sm text-slate-400 mt-1">
              {metrics?.overall?.estimated_weeks_to_full_compliance 
                ? `~${metrics.overall.estimated_weeks_to_full_compliance} weeks` 
                : 'Increase velocity to estimate'}
            </div>
          </div>
        </div>
      </div>

      {/* Framework Breakdown */}
      <h2 className="text-xl font-semibold mb-4">Framework Progress</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {metrics?.by_framework?.map((fw, idx) => (
          <div key={idx} className="bg-white border border-slate-200 rounded-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${getFrameworkColor(fw.framework_name)}`}></div>
                <h3 className="font-semibold text-lg">{fw.framework_name}</h3>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold">{fw.compliance_percentage}%</div>
                <div className="text-sm text-slate-500">compliant</div>
              </div>
            </div>
            
            <ProgressBar value={fw.compliance_percentage} color={getFrameworkColor(fw.framework_name)} />
            
            <div className="grid grid-cols-3 gap-4 mt-4 text-center">
              <div className="bg-slate-50 rounded-sm p-3">
                <div className="text-lg font-semibold text-slate-700">{fw.total_controls}</div>
                <div className="text-xs text-slate-500">Total</div>
              </div>
              <div className="bg-emerald-50 rounded-sm p-3">
                <div className="text-lg font-semibold text-emerald-600">{fw.compliant_controls}</div>
                <div className="text-xs text-emerald-600">Done</div>
              </div>
              <div className="bg-amber-50 rounded-sm p-3">
                <div className="text-lg font-semibold text-amber-600">{fw.remaining_controls}</div>
                <div className="text-xs text-amber-600">Remaining</div>
              </div>
            </div>

            {fw.estimated_completion_date && (
              <div className="mt-4 flex items-center gap-2 text-sm text-slate-600 bg-blue-50 rounded-sm p-3">
                <Calendar size={16} className="text-blue-500" />
                <span>Est. completion: <strong>{fw.estimated_completion_date}</strong></span>
                {fw.estimated_weeks_to_completion && (
                  <span className="text-slate-400">({fw.estimated_weeks_to_completion} weeks)</span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Recommendations */}
      <div className="bg-amber-50 border border-amber-200 rounded-sm p-6">
        <h3 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
          <Target size={20} />
          Recommendations to Improve Velocity
        </h3>
        <ul className="space-y-2">
          {metrics?.recommendations?.map((rec, idx) => (
            <li key={idx} className="flex items-start gap-2 text-amber-800">
              <ArrowRight size={16} className="mt-0.5 flex-shrink-0" />
              <span>{rec}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Quick Actions */}
      <div className="mt-8 grid grid-cols-3 gap-4">
        <a href="/controls" className="block bg-white border border-slate-200 rounded-sm p-6 hover:border-slate-400 transition-colors">
          <CheckCircle size={24} className="text-emerald-500 mb-3" />
          <h4 className="font-semibold mb-1">Update Controls</h4>
          <p className="text-sm text-slate-500">Mark controls as compliant to increase velocity</p>
        </a>
        <a href="/evidence" className="block bg-white border border-slate-200 rounded-sm p-6 hover:border-slate-400 transition-colors">
          <Target size={24} className="text-blue-500 mb-3" />
          <h4 className="font-semibold mb-1">Add Evidence</h4>
          <p className="text-sm text-slate-500">Upload evidence to support control compliance</p>
        </a>
        <a href="/bulk-import" className="block bg-white border border-slate-200 rounded-sm p-6 hover:border-slate-400 transition-colors">
          <TrendingUp size={24} className="text-purple-500 mb-3" />
          <h4 className="font-semibold mb-1">Bulk Import</h4>
          <p className="text-sm text-slate-500">Import multiple evidence items at once</p>
        </a>
      </div>
    </DashboardLayout>
  );
}
